import React, { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { getClientIdentityHeaders } from "@/lib/clientIdentity";
import type { ResearchManifestSummary } from "@shared/researchManifest";

const statusCopy: Record<ResearchManifestSummary["status"], { title: string; detail: string }> = {
  captured: { title: "Evidence capture saved", detail: "This is the evidence and metadata known when the report was saved. It does not establish model accuracy." },
  "metadata-incomplete": { title: "Historical capture metadata is incomplete", detail: "No missing history has been reconstructed. The saved report remains available." },
  disabled: { title: "Evidence capture was disabled", detail: "This report has no frozen evidence manifest. The saved report remains available." },
  "integrity-failed": { title: "Evidence capture integrity check failed", detail: "The manifest is unavailable. The saved report remains available." },
  "unsupported-schema": { title: "Evidence capture format is unsupported", detail: "This app cannot read the saved manifest format. The saved report remains available." },
};

export async function downloadResearchManifest(reportId: string): Promise<void> {
  const response = await fetch(`/api/reports/${encodeURIComponent(reportId)}/manifest`, {
    headers: getClientIdentityHeaders(),
    credentials: "include",
  });
  if (!response.ok) throw new Error("Manifest export is unavailable");
  const content = await response.blob();
  const url = URL.createObjectURL(content);
  const link = document.createElement("a");
  link.href = url;
  link.download = `tsm-${reportId}-research-manifest.json`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function ResearchManifestEvidence({
  reportId,
  summary,
  canExport,
}: {
  reportId: string;
  summary: ResearchManifestSummary;
  canExport: boolean;
}) {
  const [state, setState] = useState<"idle" | "working" | "error">("idle");
  const copy = statusCopy[summary.status];
  const handleExport = async () => {
    setState("working");
    try {
      await downloadResearchManifest(reportId);
      setState("idle");
    } catch {
      setState("error");
    }
  };

  return <section aria-labelledby="manifest-evidence-title" style={{ margin: "12px 0", padding: "12px", border: "1px solid var(--border, #ccc)", borderRadius: "8px" }}>
    <h3 id="manifest-evidence-title">Research evidence capture</h3>
    <p><strong>{copy.title}.</strong> {copy.detail}</p>
    {summary.version && <p>Format: {summary.version}</p>}
    {summary.digest && <p>Evidence digest: <code>{summary.digest}</code></p>}
    {summary.reasons.length > 0 && <ul>{summary.reasons.map((reason, index) => <li key={`${index}-${reason}`}>{reason}</li>)}</ul>}
    {summary.status === "captured" && canExport && <button type="button" className="analytics-action-btn" onClick={handleExport} disabled={state === "working"}>
      {state === "working" ? <Loader2 size={14} className="analytics-spin" aria-hidden="true" /> : <Download size={14} aria-hidden="true" />}
      <span>{state === "working" ? "Preparing JSON…" : state === "error" ? "JSON export failed" : "Download evidence JSON"}</span>
    </button>}
  </section>;
}
